import type { MappingException, MappingResolution } from '@fruuit/shared'
import {
  Button,
  Callout,
  DataTable,
  Dialog,
  DialogCancel,
  EmptyState,
  Input,
  Select,
  StatusBadge,
  Textarea,
  useToast,
} from '@fruuit/ui'
import { CircleCheck } from 'lucide-react'
import { type FormEvent, useState } from 'react'
import { errorMessage, isApiError } from '../../api/client'
import { formatDate } from '../coverage/format'
import { useMappingCandidates, useMappingExceptions, useResolveException } from './api'

const REASON_LABEL: Record<MappingException['reason'], string> = {
  unresolved: 'Not in the security master',
  needs_review: 'Needs manager review',
  conflict: 'Conflicting mapping',
}

/** Mapping-exception queue for a client (P-COV-002, GAP-UI-021). */
export function MappingExceptionsTab({
  org,
  canResolve,
  focusId,
}: {
  org: string
  canResolve: boolean
  focusId?: string | null
}) {
  const [status, setStatus] = useState('open')
  const [cursors, setCursors] = useState<string[]>([])
  const list = useMappingExceptions(org, status, cursors[cursors.length - 1] ?? null)
  const [resolving, setResolving] = useState<MappingException | null>(null)
  const items = list.data?.items ?? []
  const focused = focusId ? items.find((e) => e.id === focusId) : undefined
  return (
    <div className="space-y-4">
      <div className="max-w-xs">
        <Select
          label="Status"
          value={status}
          onChange={(event) => {
            setStatus(event.target.value)
            setCursors([])
          }}
          placeholder="All statuses"
          options={[
            { value: 'open', label: 'Open' },
            { value: 'resolved', label: 'Resolved' },
            { value: 'dismissed', label: 'Dismissed' },
          ]}
        />
      </div>
      {list.error ? (
        <Callout tone="danger" role="alert">
          {errorMessage(list.error)}
        </Callout>
      ) : null}
      <DataTable
        caption="Mapping exceptions"
        rows={items}
        getRowId={(e) => e.id}
        loading={list.isPending}
        empty={
          <EmptyState
            variant="plain"
            icon={<CircleCheck />}
            title={status === 'open' ? 'No open mapping exceptions' : 'No exceptions match'}
            description="Every holding in view maps to a covered company or has been resolved."
          />
        }
        pagination={{
          mode: 'server',
          hasNext: Boolean(list.data?.next_cursor),
          hasPrevious: cursors.length > 0,
          onNext: () => {
            const next = list.data?.next_cursor
            if (next) setCursors((c) => [...c, next])
          },
          onPrevious: () => setCursors((c) => c.slice(0, -1)),
        }}
        columns={[
          {
            id: 'identifier',
            header: 'Identifier',
            cell: (e) => (
              <div className="min-w-[11rem]">
                <span className="font-semibold text-ink">{e.identifier_value}</span>
                <span className="block text-xs text-sage">
                  {e.identifier_type}
                  {e.issuer_name_hint ? ` · Client name: ${e.issuer_name_hint}` : ''}
                </span>
              </div>
            ),
          },
          {
            id: 'portfolio',
            header: 'Portfolio',
            cell: (e) => (
              <div>
                {e.portfolio}
                <span className="block text-xs text-sage">Position {formatDate(e.as_of)}</span>
              </div>
            ),
          },
          { id: 'reason', header: 'Reason', cell: (e) => REASON_LABEL[e.reason] },
          {
            id: 'status',
            header: 'Status',
            cell: (e) => (
              <div className="min-w-[10rem]">
                <StatusBadge status={e.holding_mapping_state} />
                {e.status !== 'open' ? (
                  <span className="mt-1 block text-xs text-sage">
                    {e.status === 'resolved' ? 'Resolved' : 'Dismissed'}
                    {e.resolved_company ? ` → ${e.resolved_company}` : ''}
                    {e.resolved_by_name ? ` by ${e.resolved_by_name}` : ''} ·{' '}
                    {formatDate(e.resolved_at)}
                  </span>
                ) : (
                  <span className="mt-1 block text-xs text-sage">
                    Raised {formatDate(e.created_at)}
                  </span>
                )}
              </div>
            ),
          },
          {
            id: 'action',
            header: <span className="sr-only">Action</span>,
            align: 'right',
            cell: (e) =>
              e.status === 'open' && canResolve ? (
                <Button
                  size="sm"
                  variant={focused?.id === e.id ? 'primary' : 'secondary'}
                  onClick={() => setResolving(e)}
                >
                  Resolve
                </Button>
              ) : null,
          },
        ]}
      />
      {!canResolve ? (
        <p className="text-xs text-sage">Only Fruuit managers resolve mapping exceptions.</p>
      ) : null}
      <ResolveDialog exception={resolving} org={org} onClose={() => setResolving(null)} />
    </div>
  )
}

function ResolveDialog({
  exception,
  org,
  onClose,
}: {
  exception: MappingException | null
  org: string
  onClose: () => void
}) {
  const [action, setAction] = useState<MappingResolution>('link')
  const [search, setSearch] = useState('')
  const [securityId, setSecurityId] = useState('')
  const [note, setNote] = useState('')
  const candidates = useMappingCandidates(org, exception?.id ?? null, search)
  const resolve = useResolveException(org)
  const { toast } = useToast()

  function close() {
    setAction('link')
    setSearch('')
    setSecurityId('')
    setNote('')
    resolve.reset()
    onClose()
  }

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (!exception) return
    const body =
      action === 'link'
        ? { action, revision: exception.revision, security_id: securityId, note }
        : { action, revision: exception.revision, note }
    try {
      await resolve.mutateAsync({ id: exception.id, body })
      toast({ tone: 'success', message: `Mapping for ${exception.identifier_value} updated.` })
      close()
    } catch {
      // Shown below.
    }
  }

  const fieldErrors = isApiError(resolve.error) ? resolve.error.fieldErrors : {}
  return (
    <Dialog
      open={exception !== null}
      onClose={close}
      title="Resolve mapping exception"
      description={
        exception
          ? `${exception.identifier_type} ${exception.identifier_value} in ${exception.portfolio}`
          : ''
      }
      footer={
        <>
          <DialogCancel />
          <Button
            type="submit"
            form="resolve-form"
            loading={resolve.isPending}
            disabled={action === 'link' && !securityId}
          >
            {action === 'link'
              ? 'Link security'
              : action === 'out_of_scope'
                ? 'Mark out of scope'
                : 'Dismiss'}
          </Button>
        </>
      }
    >
      <form id="resolve-form" onSubmit={submit} className="space-y-4">
        <Select
          label="Resolution"
          value={action}
          onChange={(event) => setAction(event.target.value as MappingResolution)}
          options={[
            { value: 'link', label: 'Link to a covered security' },
            { value: 'out_of_scope', label: 'Outside coverage (for example a municipal bond)' },
            { value: 'dismiss', label: 'Dismiss without changing the holding' },
          ]}
        />
        {action === 'link' ? (
          <>
            <Input
              label="Find a security"
              hint="Search by identifier or company name. Matches are never applied automatically."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={exception?.issuer_name_hint ?? 'ISIN or company'}
            />
            <fieldset className="max-h-56 space-y-1 overflow-auto rounded-md border border-line p-2">
              <legend className="sr-only">Candidate securities</legend>
              {(candidates.data?.items ?? []).length === 0 ? (
                <p className="p-2 text-sm text-sage">
                  {candidates.isFetching ? 'Searching…' : 'No matching covered securities.'}
                </p>
              ) : (
                candidates.data?.items.map((c) => (
                  <label
                    key={c.security_id}
                    className="flex cursor-pointer items-start gap-2 rounded p-2 text-sm hover:bg-wash"
                  >
                    <input
                      type="radio"
                      name="security"
                      value={c.security_id}
                      checked={securityId === c.security_id}
                      onChange={() => setSecurityId(c.security_id)}
                      className="mt-1"
                    />
                    <span>
                      <span className="font-semibold text-ink">{c.company}</span>
                      <span className="block text-xs text-sage">
                        {c.identifier_type} {c.identifier_value}
                        {c.name ? ` · ${c.name}` : ''} · {c.country}
                      </span>
                    </span>
                  </label>
                ))
              )}
            </fieldset>
            {fieldErrors.security_id ? (
              <p className="text-sm text-danger">{fieldErrors.security_id}</p>
            ) : null}
          </>
        ) : null}
        <Textarea
          label="Reason"
          hint="Recorded in the audit trail (8 characters or more)."
          required
          minLength={8}
          value={note}
          onChange={(event) => setNote(event.target.value)}
          error={fieldErrors.note}
        />
        {resolve.error && !fieldErrors.note && !fieldErrors.security_id ? (
          <Callout tone="danger" role="alert">
            {errorMessage(resolve.error)}
          </Callout>
        ) : null}
      </form>
    </Dialog>
  )
}
